import {
  Suspense,
  createElement,
  lazy,
  use,
  useEffect,
  useLayoutEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'
import { DialogEnterContext, createEnterHandover } from '@/components/ui/dialog-surface'
import { createPortal } from 'react-dom'

/**
 * A local-state overlay (a confirm, a sheet) whose surface is fetched on first open instead of with the page.
 * `preload()` is for intent (the menu that may end in it opening, a touch on its trigger); once loaded, it renders
 * synchronously, so a warm open never suspends.
 */
export interface LazyOverlay<P extends object> {
  (props: P): React.ReactNode
  preload: () => void
  isLoaded: () => boolean
}

export function lazyOverlay<P extends object>(load: () => Promise<ComponentType<P>>): LazyOverlay<P> {
  let loaded: ComponentType<P> | null = null
  let pending: Promise<ComponentType<P>> | null = null
  const fetch = () =>
    (pending ??= load().then(
      (component) => (loaded = component),
      (error: unknown) => {
        pending = null
        throw error
      },
    ))
  const Suspending = lazy(() => fetch().then((component) => ({ default: component })))
  // The loaded component directly, not the lazy one: React treats a lazy it has not rendered yet as pending for a
  // commit even when its promise has long resolved, which would flash the skeleton on a preloaded open.
  const Overlay = (props: P) => createElement(loaded ?? Suspending, props)
  return Object.assign(Overlay, {
    preload: () => void fetch().catch(() => {}),
    isLoaded: () => loaded !== null,
  })
}

/** `mounted` flips on at the first open and never back, so the exit still plays (AGENTS.md: lazy overlays). */
export function useLazyOverlay(open: boolean) {
  const [mounted, setMounted] = useState(false)
  if (open && !mounted) setMounted(true)
  return { mounted }
}

const NEVER = new Promise<never>(() => {})
/** Keeps the boundary on its fallback while a skeleton plays its exit. */
function Suspended(): ReactNode {
  return use(NEVER)
}

/**
 * The Suspense around a lazy overlay's surface. While the code is fetched its skeleton stands in, and hands its enter
 * to the surface (ui/dialog-surface.ts). Closed before the code arrived, the skeleton stays up for `exitMs` with
 * `open` false, playing the surface's exit instead of vanishing.
 */
export function LazyOverlaySuspense(props: {
  open: boolean
  exitMs: number
  skeleton: (open: boolean) => ReactNode
  children: ReactNode
}) {
  const [handover] = useState(createEnterHandover)
  const [skeletonUp, setSkeletonUp] = useState(false)
  const [exiting, setExiting] = useState(false)
  const [wasOpen, setWasOpen] = useState(props.open)
  if (props.open !== wasOpen) {
    setWasOpen(props.open)
    setExiting(!props.open && skeletonUp)
  }
  useEffect(() => {
    if (!exiting) return
    const unmount = setTimeout(() => setExiting(false), props.exitMs)
    return () => clearTimeout(unmount)
  }, [exiting, props.exitMs])

  return (
    <DialogEnterContext value={handover}>
      <Suspense
        fallback={
          (props.open || exiting) && <SkeletonUp onChange={setSkeletonUp}>{props.skeleton(props.open)}</SkeletonUp>
        }
      >
        {exiting ? <Suspended /> : props.children}
      </Suspense>
    </DialogEnterContext>
  )
}

function SkeletonUp(props: { onChange: (up: boolean) => void; children: ReactNode }) {
  const { onChange } = props
  useLayoutEffect(() => {
    onChange(true)
    return () => onChange(false)
  }, [onChange])
  return props.children
}

/** Where a skeleton stands in for the surface: on `body`, as base-ui portals the real one, so no transformed ancestor
 *  (a nav-stack pane, a list row) turns its `fixed` box into a local one. Client-only: it renders after an open. */
export function OverlayPortal(props: { children: ReactNode }) {
  return createPortal(props.children, document.body)
}
